import { useEffect, useState, type ReactNode } from 'react';
import { failureKind, type ApiFailure } from '../api.js';
import { Button, LinkButton } from './Button.js';
import { CopyButton } from './CopyButton.js';
import { Icon, type IconName } from './Icon.js';
import { Spinner } from './Spinner.js';

export type StateKind = 'loading' | 'empty' | 'error' | 'denied' | 'not-configured' | 'unavailable' | 'not-found';

const ICONS: Record<Exclude<StateKind, 'loading'>, IconName> = {
  empty: 'circle',
  error: 'alert-circle',
  denied: 'lock',
  'not-configured': 'unplug',
  unavailable: 'cloud-off',
  'not-found': 'help-circle',
};

const DEFAULT_TITLES: Record<Exclude<StateKind, 'loading'>, string> = {
  empty: 'Nothing here yet',
  error: "We couldn't load this",
  denied: 'Access restricted',
  'not-configured': 'Not switched on',
  unavailable: 'Temporarily unavailable',
  'not-found': 'Not found',
};

/**
 * How long a load runs before any loading state is drawn (03-components §1: "~200ms — flashing a skeleton for a 40ms
 * response is worse than showing nothing"). StateView's spinner waits this long in script; every skeleton — DataTable's
 * rows and cards, StatTile's value — waits the same in CSS (`loading-reveal`, components.css), held equal by
 * test/server/client-helpers/loading-delay.test.ts (Phase 1c finding D23).
 */
export const LOADING_DELAY_MS = 200;

export interface StateViewProps {
  kind: StateKind;
  title?: ReactNode;
  description?: ReactNode;
  /** The action that fills an empty state, or any recovery control. */
  action?: ReactNode;
  /** For error/unavailable: renders a "Try again" button. */
  onRetry?: () => void;
  /** Shown selectable for error states so support can find the detail (app-baseline 05 §5.2). */
  correlationId?: string | null;
  /** Loading: the announced label. */
  label?: string;
  /** Loading: delay before anything is drawn (03-components §1: ~200 ms). */
  delayMs?: number;
  /** Reserve height so the container does not collapse and the page does not jump. */
  minHeight?: string;
  /** Compact variant for table rows and small panels. */
  compact?: boolean;
  icon?: IconName;
  className?: string;
}

/**
 * The four states beyond the happy path (03-components §1), plus not-configured and unavailable
 * (app-baseline 05-client §5.4) and not-found (§5.3). Every data-bearing region renders one of these
 * rather than a blank rectangle.
 */
export function StateView({ kind, title, description, action, onRetry, correlationId, label, delayMs = LOADING_DELAY_MS, minHeight, compact = false, icon, className }: StateViewProps) {
  const classes = ['state-view', `state-view--${kind}`, compact ? 'state-view--compact' : '', className ?? ''].filter(Boolean).join(' ');
  const style = minHeight ? { minBlockSize: minHeight } : undefined;

  if (kind === 'loading') {
    return <LoadingState label={label ?? 'Loading'} delayMs={delayMs} className={classes} style={style} />;
  }

  const heading = title ?? DEFAULT_TITLES[kind];
  const isAlert = kind === 'error' || kind === 'unavailable';
  return (
    <div className={classes} style={style} role={isAlert ? 'alert' : undefined}>
      <Icon name={icon ?? ICONS[kind]} size={compact ? 20 : 28} className="state-view__icon" />
      <p className="state-view__title">{heading}</p>
      {description ? <div className="state-view__description">{description}</div> : null}
      {correlationId ? (
        <p className="state-view__reference">
          <span className="muted text-xs">Reference </span>
          <code className="selectable text-xs">{correlationId}</code>
          <CopyButton value={correlationId} label="Copy reference" />
        </p>
      ) : null}
      {onRetry || action ? (
        <div className="state-view__actions cluster">
          {onRetry ? <Button variant="secondary" icon="refresh" onClick={onRetry}>Try again</Button> : null}
          {action}
        </div>
      ) : null}
    </div>
  );
}

function LoadingState({ label, delayMs, className, style }: { label: string; delayMs: number; className: string; style?: { minBlockSize: string } }) {
  const [visible, setVisible] = useState(delayMs <= 0);
  useEffect(() => {
    if (delayMs <= 0) return;
    const handle = window.setTimeout(() => setVisible(true), delayMs);
    return () => window.clearTimeout(handle);
  }, [delayMs]);
  return (
    <div className={className} style={style} aria-busy="true">
      {visible ? <Spinner size={24} label={label} /> : null}
      {visible ? <p className="state-view__title muted">{label}…</p> : null}
    </div>
  );
}

export interface FailureViewProps {
  failure: ApiFailure;
  /** What was being loaded, in the user's words: "your to-dos". */
  what: string;
  onRetry?: () => void;
  /** Where to go when the thing is not found; defaults to the overview. */
  backTo?: { to: string; label: string };
  /** For not-configured: what the operator would switch on ("the Fellow integration"). */
  featureName?: string;
  compact?: boolean;
  minHeight?: string;
  className?: string;
}

/**
 * Picks the right state for an API failure so pages never render an empty list where the answer
 * is "not allowed", and never say "unavailable" about a feature that was never switched on.
 */
export function FailureView({ failure, what, onRetry, backTo, featureName, compact, minHeight, className }: FailureViewProps) {
  const kind = failureKind(failure);
  switch (kind) {
    case 'denied':
      return (
        <StateView
          kind="denied"
          title="Access restricted"
          description={`You do not have access to ${what}. ${DENIED_NEXT_STEP}`}
          compact={compact}
          minHeight={minHeight}
          className={className}
        />
      );
    case 'not-configured':
      return (
        <StateView
          kind="not-configured"
          title={`${capitalise(featureName ?? what)} is not switched on`}
          description="An operator can switch it on."
          compact={compact}
          minHeight={minHeight}
          className={className}
        />
      );
    case 'not-found':
      return (
        <StateView
          kind="not-found"
          title={`We couldn't find ${what}`}
          description="It may have been removed, or the link may be out of date."
          action={<LinkButton to={backTo?.to ?? '/'} variant="secondary">{backTo?.label ?? 'Go to overview'}</LinkButton>}
          compact={compact}
          minHeight={minHeight}
          className={className}
        />
      );
    case 'unavailable':
      return (
        <StateView
          kind="unavailable"
          title={`${capitalise(what)} can't be loaded right now`}
          description={failure.message}
          onRetry={onRetry}
          correlationId={failure.correlationId}
          compact={compact}
          minHeight={minHeight}
          className={className}
        />
      );
    case 'error':
    default:
      return (
        <StateView
          kind="error"
          title={`We couldn't load ${what}`}
          description={failure.message}
          onRetry={onRetry}
          correlationId={failure.correlationId}
          compact={compact}
          minHeight={minHeight}
          className={className}
        />
      );
  }
}

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * FailureView's next step for a refusal (03-components §1, the denied state; 05-content §4's third question). A refused
 * action's toast says it too (`failureFollowUp`), so a refusal names the way forward wherever the reader meets it.
 */
export const DENIED_NEXT_STEP = 'Ask an administrator to add you to a group that grants it.';

/** A refusal after the call's own sentence: FailureView's denied copy, the thing refused being the call itself. */
const DENIED_FOLLOW_UP = `You do not have access to this. ${DENIED_NEXT_STEP}`;

/** A fault on our side after the call's own sentence: why, and what to do next — each a complete string (05-content §8). */
const OUR_SIDE_FOLLOW_UP = {
  withReference: 'The fault was on our side, not in anything you did. Try again; if it keeps happening, quote the reference.',
  withoutReference: 'The fault was on our side, not in anything you did. Try again in a moment.',
} as const;

/**
 * Why, and what to do next, said after a call's own sentence of what happened ("We couldn't start a sync.") —
 * 05-content §4's second and third questions; the call's sentence is the first. A refusal (`forbidden`) is FailureView's
 * denied copy, next step included, where the envelope's "You do not have permission to do this." names none. A fault on
 * our side (`internal_error`) is told without the envelope's "Something went wrong on our side. Please try again." —
 * §4's own No-example, and a second, vaguer "what happened" after the call's; that text stays the fallback wherever a
 * failure is shown with no sentence of its own (FailureView, an inline line under a control). Anything else is the
 * server's own message, which answers both itself ("Too many requests in a short time. Wait a moment and try again.").
 */
export function failureFollowUp(failure: Pick<ApiFailure, 'code' | 'message' | 'correlationId'>): string {
  if (failure.code === 'forbidden') return DENIED_FOLLOW_UP;
  if (failure.code === 'internal_error') return failure.correlationId ? OUR_SIDE_FOLLOW_UP.withReference : OUR_SIDE_FOLLOW_UP.withoutReference;
  return failure.message;
}

/**
 * A failed action as an inline error line under its control says it (a form's summary, a dialog's error, the line under
 * a button) — the owner's ruling Q11 of escalation 20261005-200526: "error lines under a control give the same refusal
 * next step as the pop-ups". A refusal (`forbidden`) is the toast's own denied copy, FailureView's next step included
 * (`DENIED_FOLLOW_UP`), never the envelope's "You do not have permission to do this.", which names none. Anything else is
 * the server's own message as before — a line with no sentence of its own keeps the envelope's words for a fault on our
 * side (`failureFollowUp` above). Each site adds the reference its line carries.
 */
export function inlineFailureText(failure: Pick<ApiFailure, 'code' | 'message'>): string {
  return failure.code === 'forbidden' ? DENIED_FOLLOW_UP : failure.message;
}

/**
 * A failed action as a toast says it, after the call's own sentence of what happened: `failureFollowUp`, then the
 * reference support can find, selectable (05-content §4: "do provide a support reference where one exists").
 */
export function failureDetail(failure: Pick<ApiFailure, 'code' | 'message' | 'correlationId'>): ReactNode {
  return (
    <>
      {failureFollowUp(failure)}
      {failure.correlationId ? <span className="text-xs muted"> Reference <code className="selectable">{failure.correlationId}</code></span> : null}
    </>
  );
}
